import axios, { InternalAxiosRequestConfig } from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { getMsalInstance, get } from '../config/authConfig';

/** Anti-boucle de redirection : au plus une réauthentification par minute.
 *
 *  C'est le garde-fou qui manquait pour oser déclencher le re-login automatique.
 *  Sans lui, un échec permanent d'acquisition ferait boucler l'application entre
 *  Microsoft et AGORA sans que l'utilisateur puisse rien lire à l'écran. */
const CLE_REAUTH = 'agora:derniere-reauth';
const DELAI_REAUTH_MS = 60_000;

function reauthentifier(): void {
  const dernier = Number(sessionStorage.getItem(CLE_REAUTH) || 0);
  if (Date.now() - dernier < DELAI_REAUTH_MS) {
    console.warn('[auth] réauthentification déjà tentée il y a moins d\'une minute — on n\'insiste pas');
    return;
  }
  sessionStorage.setItem(CLE_REAUTH, String(Date.now()));
  console.warn('[auth] session Microsoft expirée — redirection vers la reconnexion');
  getMsalInstance().acquireTokenRedirect(get.apiTokenRequest());
}

// Utiliser la variable d'environnement ou fallback vers localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Créer une instance axios avec la configuration de base
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter automatiquement le token d'authentification
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      const msalInstance = getMsalInstance();
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        const account = accounts[0];

        const tokenResponse = await msalInstance.acquireTokenSilent({
          ...get.apiTokenRequest(),
          account,
        });

        // Use access token when custom API scope is configured, otherwise ID token.
        const token = get.apiScope()
          ? tokenResponse?.accessToken
          : tokenResponse?.idToken;

        if (!token) {
          // Acquisition réussie mais sans jeton exploitable : partir quand même
          // reviendrait à envoyer une requête anonyme et à récolter un 401 muet.
          throw new InteractionRequiredAuthError('no_token', 'Aucun jeton exploitable');
        }
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      // Défaut corrigé le 04/09 : l'erreur était avalée et la requête partait SANS
      // en-tête d'autorisation. L'utilisateur restait connecté côté Microsoft mais
      // n'obtenait plus aucune donnée, sans jamais être invité à se reconnecter.
      if (error instanceof InteractionRequiredAuthError) {
        reauthentifier();
      } else {
        console.warn('[auth] acquisition du jeton impossible:', error);
      }
      // On n'envoie JAMAIS une requête sans jeton : mieux vaut un échec explicite
      // qu'un 401 serveur qui ressemble à un problème de droits.
      return Promise.reject(
        new axios.Cancel('Authentification requise — requête non envoyée'),
      );
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur response : diagnosticabilité centralisée des 401/403/réseau.
// Re-rejette TOUJOURS — l'appelant garde la main sur son erreur. Le re-login sur 401,
// laissé en suspens jusqu'ici « par risque de boucle de redirection », est désormais
// possible : `reauthentifier()` est plafonné à une tentative par minute.
// Filet de second rideau : le cas normal est traité en amont, à l'acquisition du jeton.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url;
    if (status === 401) {
      console.warn('[auth] 401 — session/token rejeté par l\'API:', url);
      reauthentifier();
    } else if (status === 403) {
      console.warn('[auth] 403 — accès refusé:', url);
    } else if (!error?.response) {
      console.warn('[réseau] requête API échouée (réseau/timeout):', url);
    }
    return Promise.reject(error);
  }
);

export default api;
